import { productFieldOf } from "./catalog";
import {
  FILTER_ORDER,
  FILTER_REGISTRY,
  resolve,
  type ResolvedFilters,
} from "./filters";

export type MergeTagBuilder = (
  identifier: string,
  filters?: ResolvedFilters,
) => string;

const NESTED_TILE_DATA_PAYLOAD_PATTERN =
  /^tile_data\.(?:metadata|event_data)\..+/;

function nestedTileDataParentPaths(identifier: string): string[] {
  if (!NESTED_TILE_DATA_PAYLOAD_PATTERN.test(identifier)) return [];

  const segments = identifier.split(".");
  const parents: string[] = [];
  for (let length = 2; length < segments.length; length += 1) {
    parents.push(segments.slice(0, length).join("."));
  }
  return parents;
}

function wrapNestedTileDataAccess(
  identifier: string,
  inner: string,
  fallback: string | undefined,
): string {
  const parents = nestedTileDataParentPaths(identifier);
  if (parents.length === 0) return inner;

  const conditions =
    fallback === undefined ? parents : [...parents, identifier];
  return `{% if ${conditions.join(" and ")} %}${inner}{% else %}${
    fallback ?? ""
  }{% endif %}`;
}

/** Show as, which the builders place, never chain. */
const isPlacedFilter = (id: (typeof FILTER_ORDER)[number]) =>
  FILTER_REGISTRY[id].placement === "display";

/**
 * A field that renders only above 0 (review totals: "" or "0" mean none), so
 * a product with no reviews renders nothing, never "0.0". `|float` because
 * the values are strings and `""|float` is 0 in gonja. Stars loop over
 * `range`, which is safe for any count, where `"★" * n` panics on a
 * negative one. `|int` truncates, so stars are the whole stars earned (4.7
 * shows 4) and never overstate the average.
 *
 *   {% if P|float > 0 %}{{P}}{% endif %}
 *   {% if P|float > 0 %}{% for i in range(P|float|int) %}★{% endfor %}{% endif %}
 *
 * Null for any other field, which keeps its usual output.
 */
function positiveOnlyOutput(
  identifier: string,
  resolved: ResolvedFilters,
  value: string,
  elseBranch: string | undefined,
): string | null {
  if (!productFieldOf(identifier)?.hideUnlessPositive) return null;
  const number = `${identifier}|float`;
  const shown =
    FILTER_REGISTRY.ratingDisplay.build(resolved.ratingDisplay) === "stars"
      ? `{% for i in range(${number}|int) %}★{% endfor %}`
      : value;
  const otherwise = elseBranch === undefined ? "" : `{% else %}${elseBranch}`;
  return `{% if ${number} > 0 %}${shown}${otherwise}{% endif %}`;
}

/**
 * Current wire syntax: `{{<id>|<filters>}}` with the filter chain in
 * {@link FILTER_ORDER} (e.g. `{{customer.first_name|default:"there"|capfirst}}`).
 */
export const liquidFilterChainBuilder: MergeTagBuilder = (
  identifier,
  filters,
) => {
  const resolved = resolve(filters);
  const segments: string[] = [identifier];

  for (const id of FILTER_ORDER) {
    const value = resolved[id];
    if (value === undefined || isPlacedFilter(id)) continue;
    const segment = FILTER_REGISTRY[id].build(value);
    if (segment) segments.push(segment);
  }
  segments.push(...(resolved.passthrough ?? []));

  const positiveOnly = positiveOnlyOutput(
    identifier,
    resolved,
    `{{${segments.join("|")}}}`,
    undefined,
  );
  if (positiveOnly) return positiveOnly;

  return wrapNestedTileDataAccess(
    identifier,
    `{{${segments.join("|")}}}`,
    typeof resolved.default === "string" ? resolved.default : undefined,
  );
};

export const ifElseWrapperBuilder: MergeTagBuilder = (identifier, filters) => {
  const resolved = resolve(filters);
  const segments: string[] = [identifier];
  let elseBranch: string | undefined;

  for (const id of FILTER_ORDER) {
    const def = FILTER_REGISTRY[id];
    const value = resolved[id];
    if (value === undefined) continue;
    if (def.placement === "else") {
      elseBranch = typeof value === "string" ? value : "";
      continue;
    }
    if (isPlacedFilter(id)) continue;
    const segment = def.build(value);
    if (segment) segments.push(segment);
  }
  segments.push(...(resolved.passthrough ?? []));

  const inner = `{{${segments.join("|")}}}`;
  const positiveOnly = positiveOnlyOutput(
    identifier,
    resolved,
    inner,
    elseBranch,
  );
  if (positiveOnly) return positiveOnly;
  if (nestedTileDataParentPaths(identifier).length > 0) {
    return wrapNestedTileDataAccess(identifier, inner, elseBranch);
  }
  return elseBranch === undefined
    ? inner
    : `{% if ${identifier} %}${inner}{% else %}${elseBranch}{% endif %}`;
};

/** The builder the whole app currently routes through. */
export const ACTIVE_BUILDER: MergeTagBuilder = liquidFilterChainBuilder;

/** Builds wire syntax for an identifier + optional filters via {@link ACTIVE_BUILDER}. */
export function build(identifier: string, filters?: ResolvedFilters): string {
  return ACTIVE_BUILDER(identifier, filters);
}
